import { useState } from 'react';
import { KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';

const formatError = (detail) => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return 'Could not change password';
};

export const ChangePasswordDialog = ({ api }) => {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setCurrent(''); setNext(''); setConfirm(''); setError(''); };

  const submit = async (event) => {
    event.preventDefault();
    if (next !== confirm) { setError('New passwords do not match'); return; }
    if (next.length < 8) { setError('New password must be at least 8 characters'); return; }
    setSaving(true);
    setError('');
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      toast.success('Password updated. Use the new password next time you sign in.');
      reset();
      setOpen(false);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => { setOpen(value); if (!value) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="h-12 rounded-full border-[#0B1F3B]/20 px-6 text-[#0B1F3B] hover:bg-[#EFE6D8]" data-testid="admin-change-password-btn"><KeyRound className="h-4 w-4" />Change password</Button>
      </DialogTrigger>
      <DialogContent className="rounded-[2rem] sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0B1F3B]">Change admin password</DialogTitle>
          <DialogDescription>Takes effect immediately for the next sign-in. Minimum 8 characters.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-3">
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Current password" autoComplete="current-password" required className="h-12 rounded-full" data-testid="change-password-current" />
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder="New password" autoComplete="new-password" required className="h-12 rounded-full" data-testid="change-password-new" />
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" autoComplete="new-password" required className="h-12 rounded-full" data-testid="change-password-confirm" />
          {error && <p className="rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="change-password-error">{error}</p>}
          <Button type="submit" disabled={saving} className="h-12 rounded-full bg-[#0B1F3B] text-white hover:bg-[#06101E]" data-testid="change-password-submit">{saving ? 'Saving...' : 'Update password'}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
